import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Iconify from "../../iconify/Iconify";

const createEmptyPricingOption = () => ({
  optionId: "",
  label: "",
  description: "",
  amount: "",
});

export default function StepPricingStandard({ formData, errors, onChange }) {
  const isFeeBased = Boolean(formData.isFeeBased);
  const pricingOptions = Array.isArray(formData.pricingOptions)
    ? formData.pricingOptions
    : [];
  const siblingDiscount = {
    enabled: Boolean(formData.siblingDiscount?.enabled),
    type: formData.siblingDiscount?.type === "fixed" ? "fixed" : "percentage",
    value: formData.siblingDiscount?.value ?? "",
  };

  const updatePricingOption = (index, field, value) => {
    const nextOptions = pricingOptions.map((option, optionIndex) =>
      optionIndex === index ? { ...option, [field]: value } : option
    );
    onChange("pricingOptions", nextOptions);
  };

  const addPricingOption = () => {
    onChange("pricingOptions", [...pricingOptions, createEmptyPricingOption()]);
  };

  const removePricingOption = (index) => {
    onChange(
      "pricingOptions",
      pricingOptions.filter((_, optionIndex) => optionIndex !== index)
    );
  };

  const updateSiblingDiscount = (patch) => {
    onChange("siblingDiscount", {
      ...siblingDiscount,
      ...patch,
    });
  };

  const handleFeeBasedToggle = (event) => {
    const nextValue = event.target.checked;
    onChange("isFeeBased", nextValue);

    if (nextValue && pricingOptions.length === 0) {
      const fallbackAmount = Number(formData.feeAmount);
      onChange("pricingOptions", [
        {
          ...createEmptyPricingOption(),
          label: "Teilnahmegebühr",
          amount: Number.isFinite(fallbackAmount) && fallbackAmount > 0 ? fallbackAmount : "",
        },
      ]);
    }

    if (!nextValue && siblingDiscount.enabled) {
      updateSiblingDiscount({ enabled: false });
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Preise</Typography>
        <Typography variant="body2" color="text.secondary">
          Definieren Sie Preisoptionen und optional einen Geschwisterrabatt für
          Standard-Events.
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderColor: isFeeBased ? "secondary.main" : "divider",
          bgcolor: isFeeBased
            ? (theme) => alpha(theme.palette.secondary.main, 0.06)
            : "background.paper",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={isFeeBased}
              onChange={handleFeeBasedToggle}
              color="secondary"
            />
          }
          label="Kostenpflichtiges Event"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: -0.5 }}>
          Aktivieren Sie diese Option, wenn Teilnehmergebühren erhoben werden.
        </Typography>
      </Paper>

      {isFeeBased ? (
        <Stack spacing={1.5}>
          <Typography variant="subtitle2">Preisoptionen</Typography>
          {pricingOptions.map((option, index) => (
            <Paper key={`${option.optionId || "new"}-${index}`} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" fontWeight={700}>
                    Option {index + 1}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removePricingOption(index)}
                    aria-label="Preisoption entfernen"
                  >
                    <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                  </IconButton>
                </Stack>

                <TextField
                  label="Titel"
                  value={option.label || ""}
                  onChange={(event) =>
                    updatePricingOption(index, "label", event.target.value)
                  }
                  fullWidth
                  required
                />

                <TextField
                  label="Beschreibung (optional)"
                  value={option.description || ""}
                  onChange={(event) =>
                    updatePricingOption(index, "description", event.target.value)
                  }
                  multiline
                  minRows={2}
                  fullWidth
                />

                <TextField
                  label="Betrag"
                  type="number"
                  value={option.amount ?? ""}
                  onChange={(event) =>
                    updatePricingOption(index, "amount", event.target.value)
                  }
                  inputProps={{ min: 0, step: "0.01" }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">EUR</InputAdornment>
                    ),
                  }}
                  fullWidth
                  required
                />
              </Stack>
            </Paper>
          ))}

          {errors.pricingOptions ? (
            <Alert severity="error">{errors.pricingOptions}</Alert>
          ) : null}

          <Box>
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:add-circle-bold" width={18} />}
              onClick={addPricingOption}
            >
              Weitere Preisoption hinzufügen
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={siblingDiscount.enabled}
                    onChange={(event) =>
                      updateSiblingDiscount({ enabled: event.target.checked })
                    }
                    color="secondary"
                  />
                }
                label="Geschwisterrabatt aktivieren"
              />

              <Collapse in={siblingDiscount.enabled}>
                <Stack spacing={1.5}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={siblingDiscount.type}
                    onChange={(_, value) => {
                      if (!value) return;
                      updateSiblingDiscount({ type: value });
                    }}
                  >
                    <ToggleButton value="percentage">Prozent</ToggleButton>
                    <ToggleButton value="fixed">Festbetrag</ToggleButton>
                  </ToggleButtonGroup>

                  <TextField
                    label={
                      siblingDiscount.type === "percentage"
                        ? "Rabatt in %"
                        : "Rabatt in EUR"
                    }
                    type="number"
                    value={siblingDiscount.value}
                    onChange={(event) =>
                      updateSiblingDiscount({ value: event.target.value })
                    }
                    inputProps={{
                      min: 0,
                      step: siblingDiscount.type === "percentage" ? "1" : "0.01",
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {siblingDiscount.type === "percentage" ? "%" : "EUR"}
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />

                  {errors.siblingDiscount ? (
                    <Alert severity="error">{errors.siblingDiscount}</Alert>
                  ) : null}

                  <Typography variant="caption" color="text.secondary">
                    Wird automatisch angewendet, wenn mehrere Mitglieder
                    desselben Vertrags angemeldet werden.
                  </Typography>
                </Stack>
              </Collapse>
            </Stack>
          </Paper>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Dieses Event wird kostenlos angeboten. Es sind keine Preisoptionen erforderlich.
        </Typography>
      )}
    </Stack>
  );
}

StepPricingStandard.propTypes = {
  formData: PropTypes.shape({
    isFeeBased: PropTypes.bool,
    feeAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricingOptions: PropTypes.arrayOf(
      PropTypes.shape({
        optionId: PropTypes.string,
        label: PropTypes.string,
        description: PropTypes.string,
        amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })
    ),
    siblingDiscount: PropTypes.shape({
      enabled: PropTypes.bool,
      type: PropTypes.oneOf(["percentage", "fixed"]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  errors: PropTypes.shape({
    pricingOptions: PropTypes.string,
    siblingDiscount: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};
